import './UISlot.css';

import { useCallback, useEffect, useState } from 'react';
import { createBrowserHistory } from 'history';
import { useDispatch, useSelector } from 'react-redux';
import { Router, Switch, Route } from 'react-router-dom';
import { Environment, SDKAction, sdk } from '@squareup/sq-addons-web-sdk';

import { useToast } from 'hooks/useToast';

import { CONTEXT, CUSTOMER, ORDER } from 'local-host-data';

import CheckingRewardsScreen from 'screens/CheckingRewards';
import ConnectionErrorScreen from 'screens/ConnectionError';
import CustomerScreen from 'screens/Customer';
import InvalidDiscountsScreen from 'screens/InvalidDiscounts';
import LoadingScreen from 'screens/Loading';
import MainScreen from 'screens/Main';
import SearchCustomersScreen from 'screens/SearchCustomers';
import SendEnrollmentLinkScreen from 'screens/SendEnrollmentLink';

import { scannedCodeSelector, setScannedCode } from 'store/barcodeScanner';
import {
  contextErrorSelector,
  fetchContext,
  isFetchingContextSelector,
  posContextSelector,
} from 'store/context';
import {
  RequestContext,
  customerSelector,
  customerContextSelector,
  getCustomer,
  getCustomerStateSelector,
  isCustomerAddedToSaleSelector,
} from 'store/customer';
import { store } from 'store/store';

const history = createBrowserHistory();

// Initialize the Square Addons SDK
const callbacks = {
  [SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED]: (scannedCode: string) => {
    // We need to explicitly create this as a reference type because React
    // useEffect does not get triggered when the new value is same as the
    // previous value. So, if we scan the same barcode twice, only the first
    // one will get triggered if this is a value type.
    // eslint-disable-next-line no-new-wrappers
    store.dispatch(setScannedCode(new String(scannedCode)));
  },
};

const UISlot = () => {
  const dispatch = useDispatch();
  const [showToast] = useToast();

  const posContext = useSelector(posContextSelector);
  const addonName = posContext?.configurations?.addonName || '';

  const scannedCode = useSelector(scannedCodeSelector);

  const customer = useSelector(customerSelector);
  const isCustomerAddedToSale = useSelector(isCustomerAddedToSaleSelector);
  const customerContext = useSelector(customerContextSelector);
  const getCustomerState = useSelector(getCustomerStateSelector);

  const isFetchingContext = useSelector(isFetchingContextSelector);

  const contextError = useSelector(contextErrorSelector);

  // eslint-disable-next-line no-new-wrappers
  const [prevScannedCode, setPrevScannedCode] = useState(new String(''));

  const initializeSdk = useCallback(() => {
    dispatch(
      fetchContext(async () => {
        const sdkEnv = process.env.SQ_ADDONS_SDK_ENVIRONMENT as string;
        return await sdk.initialize(callbacks, {
          environment:
            sdkEnv === 'LOCALHOST' ? Environment.LOCAL_HOST : Environment.POS,
          localHostData: {
            context: CONTEXT,
            order: ORDER,
            // Uncomment this line if you want to simulate customer already added.
            // customer: CUSTOMER,
          },
        });
      })
    );
  }, [dispatch]);

  useEffect(() => {
    initializeSdk();
  }, [initializeSdk]);

  useEffect(() => {
    if (addonName) {
      document.title = addonName as string;
    }
  }, [addonName])

  useEffect(() => {
    if (scannedCode && prevScannedCode !== scannedCode) {
      setPrevScannedCode(scannedCode);

      if (isCustomerAddedToSale) {
        showToast({
          message: 'There is already a customer added to this sale',
          persistent: false,
          variant: 'warning',
        });
      } else {
        dispatch(
          getCustomer(RequestContext.BARCODE_SCANNER, scannedCode as string)
        );
      }
    }
  }, [
    dispatch,
    showToast,
    scannedCode,
    prevScannedCode,
    isCustomerAddedToSale,
  ]);

  useEffect(() => {
    if (customerContext === RequestContext.BARCODE_SCANNER && customer) {
      history.push('/customer' + history.location.search);
    }
  }, [dispatch, customer, customerContext]);

  useEffect(() => {
    if (
      getCustomerState.error &&
      customerContext === RequestContext.BARCODE_SCANNER
    ) {
      showToast({
        message: 'Code not valid',
        persistent: false,
        variant: 'warning',
      });
    }
  }, [dispatch, showToast, getCustomerState.error, customerContext]);

  if (contextError) {
    return <ConnectionErrorScreen onClickTryAgain={initializeSdk} />;
  }

  if (isFetchingContext) {
    return <LoadingScreen />;
  }

  return (
    <div className="page-container">
      <Router history={history}>
        <Switch>
          <Route path="/checkout">
            <CheckingRewardsScreen />
          </Route>
          <Route path="/customer">
            <CustomerScreen />
          </Route>
          <Route path="/invalid-discounts">
            <InvalidDiscountsScreen />
          </Route>
          <Route path="/search-customers">
            <SearchCustomersScreen />
          </Route>
          <Route path="/send-enrollment-link">
            <SendEnrollmentLinkScreen />
          </Route>
          <Route path="/">
            <MainScreen />
          </Route>
        </Switch>
      </Router>
    </div>
  );
};

export default UISlot;
